import {LibraryAddRounded} from "@mui/icons-material";
import {Button, Checkbox, Chip, Grid, ListItemText, MenuItem, Stack, TextField} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import SectionHeader from "../../../../util/component/SectionHeader";
import {AgGridReact} from "ag-grid-react";
import '../../style/SalesMarketing.css';
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import {ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams} from "ag-grid-enterprise";
import dayjs from "dayjs";
import {useCouponActions} from "../../service/useCouponActions";
import CouponCampaignDetailModal from "./modals/CouponCampaignDetailModal";
import {useStatusValuesCouponCampaign} from "../../component/useStatusValuesCouponCampaign";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import AdvancedFilter from "../../component/AdvancedFilter";
import {
    COUPON_CAMPAIGN_STATUS_ACTIVE,
    COUPON_CAMPAIGN_STATUS_DISMISSED,
    COUPON_CAMPAIGN_STATUS_DRAFT
} from "../../component/Constants";
import * as Yup from 'yup';
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {AllCouponFilters, Coupon, CouponInputGetAll} from "./interfaces/CouponInterfaces";
import EmptyState from "../../../../util/component/EmptyState";
import ActionButtonWithTooltip, {ActionTypes} from "../../component/ActionButtonWithTooltip";
import {useHasScope} from "../../../../config/globalConfig";
import AsyncAutocomplete from "../../component/AsyncAutocomplete";

const CouponCampaignListPage = () => {
    const {t} = useTranslation(['salesmarketing']);
    const {globalData} = useContext(GlobalContext);

    const navigate = useNavigate();
    const {getCouponCampaignList, getAllCouponCodes} = useCouponActions();
    const {getStatusColor, getStatusLabel} = useStatusValuesCouponCampaign();
    const [openDetail, setOpenDetail] = useState(false);
    const [selectedCouponCampaign, setSelectedCouponCampaign] = useState<any>(null);
    const [filters, setFilters] = useState<AllCouponFilters>({
        tenantId: globalData?.user?.legalEntities?.map((le: any) => le.name),
        dealerId: globalData?.user?.dealers?.map((d: any) => d.name),
        productCode: "", status: []
    });
    const [searchInput, setSearchInput] = useState("");
    const gridRef = useRef<AgGridReact>(null);
    const hasScopes = useHasScope;


    const defaultFilters = {
        legalEntity: globalData?.user?.legalEntities?.map((le: any) => le.name),
        dealer: globalData?.user?.dealers?.map((d: any) => d.name),
        productCode: "",
        status: [],
        couponCode:[]
    }

    const validationObj = {
        productCode: Yup.string().nullable(),
        status: Yup.array(),
        couponCode : Yup.array()
    }

    const statuses = [
        {
            value: COUPON_CAMPAIGN_STATUS_DRAFT,
            label: getStatusLabel(COUPON_CAMPAIGN_STATUS_DRAFT),
            color: getStatusColor(COUPON_CAMPAIGN_STATUS_DRAFT)
        },
        {
            value: COUPON_CAMPAIGN_STATUS_ACTIVE,
            label: getStatusLabel(COUPON_CAMPAIGN_STATUS_ACTIVE),
            color: getStatusColor(COUPON_CAMPAIGN_STATUS_ACTIVE)
        },
        {
            value: COUPON_CAMPAIGN_STATUS_DISMISSED,
            label: getStatusLabel(COUPON_CAMPAIGN_STATUS_DISMISSED),
            color: getStatusColor(COUPON_CAMPAIGN_STATUS_DISMISSED)
        },
    ];

    const fields = {
        productCode: (field: any) => (
            <TextField
                fullWidth
                label={t('coupon.filtersModal.productCodeField')}
                value={field.value}
                onChange={field.onChange}
            />
        ),
        status: (field: any) => (
            <TextField
                fullWidth
                label={t('coupon.filtersModal.statusField')}
                select
                SelectProps={{
                    multiple: true,
                    renderValue: (selected: any) => selected.map((s: any) => statuses.find(ss => ss.value === s)!.label).join(', ')
                }}
                value={field.value}
                onChange={field.onChange}
            >
                {statuses?.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                        <Checkbox checked={field.value.indexOf(status.value) > -1}/>
                        <span style={{
                            border: "1px solid " + status.color,
                            backgroundColor: status.color,
                            borderRadius: "50%",
                            display: "inline-block",
                            height: "14px",
                            width: "14px",
                            marginRight: "5px"
                        }}/>
                        <ListItemText primary={status.label}/>
                    </MenuItem>
                ))}
            </TextField>
        ),
        couponCode: (field: any) => (
            <AsyncAutocomplete
                id={"couponCode"}
                multiple={true}
                value={field.value}
                callback={(val:string)=>{return getAllCouponCodes(val)}}
                onChange={(value) => field.onChange(value)}
                renderInput={(params) => <TextField
                    label={t('coupon.filtersModal.couponCode')}
                    {...params}
                />}
            />
        )
    }


    const toolbar = {
        renderActions: () =>
            hasScopes('SCOPE_SM_CREATE_CAMPAIGN') ?
                <Button fullWidth  size="small"  style={{whiteSpace: "nowrap"}} onClick={() => {
                    navigate('/salesmarketing/couponcampaigncreate')
                }}>
                    <LibraryAddRounded style={{paddingRight: "10px"}}/>
                    {t('coupon.buttons.newCampaign')}
                </Button>
                : <></>
    }

    const columns: ColDef[] = [
        {
            field: "tenantId",
            headerName: t('legalEntityField'),
            tooltipField: "legalEntity",
            hide: globalData?.user?.legalEntities?.length <= 1,
            unSortIcon: true
        },
        {
            field: "dealerId",
            headerName: t('dealerField'),
            tooltipField: "dealer",
            hide: globalData?.user?.dealers?.length <= 1,
            unSortIcon: true
        },
        {
            field: "productCode",
            headerName: t('coupon.listColumns.productCode'),
            pinned: 'left',
            lockPosition: 'left',
            lockPinned: true,
            unSortIcon: true
        },
        {
            field: "description",
            headerName: t('coupon.listColumns.description'),
            tooltipField: "description",
            pinned: 'left',
            lockPosition: 'left',
            lockPinned: true,
            unSortIcon: true
        },
        {
            field: "bookingStartDate",
            headerName: t('coupon.listColumns.bookingStartDate'),
            cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L") : "",
            unSortIcon: true
        },
        {
            field: "bookingEndDate",
            headerName: t('coupon.listColumns.bookingEndDate'),
            cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L") : "",
            unSortIcon: true
        },
        {
            field: "pickupStartDate",
            headerName: t('coupon.listColumns.pickupStartDate'),
            cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L") : "",
            unSortIcon: true
        },
        {
            field: "pickupEndDate",
            headerName: t('coupon.listColumns.pickupEndDate'),
            cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L") : "",
            unSortIcon: true
        },
        {
            field: "availableCodes",
            pinned: 'right',
            maxWidth: 180,
            lockPosition: 'right',
            lockPinned: true,
            valueGetter: (params: any) => ((params.data.couponTotal != null ? params.data.couponTotal : 0) - (params.data.couponTotal != null ? params.data.couponUsed : 0)) + "/" + (params.data.couponTotal != null ? params.data.couponTotal : 0),
            headerName: t('coupon.listColumns.availableCodes'),
            sortable: false,
        },
        {
            field: "status",
            headerName: t('coupon.listColumns.status'),
            pinned: 'right',
            lockPosition: 'right',
            maxWidth: 180,
            lockPinned: true,
            unSortIcon: true,
            cellRenderer: (params: any) => {
                return <Chip label={getStatusLabel(params.data.status)}
                             sx={{backgroundColor: getStatusColor(params.data.status)}}/>
            }
        },
        {
            field: "_actions",
            headerName: t('coupon.listColumns.action'),
            maxWidth: 180,
            pinned: 'right',
            lockPosition: 'right',
            lockPinned: true,
            sortable: false,
            cellRenderer: (params: { data: Coupon }) => {
                return (
                    <Stack direction={"row"} justifyContent={"center"}>
                        <ActionButtonWithTooltip type={ActionTypes.edit} callback={() => {
                            //navigate(`/salesmarketing/couponcampaignupdate/${params.data.id}`, {state: {promo: params.data}})
                            window.open(`/salesmarketing/couponcampaignupdate/${params.data.id}`, "_blank")
                        }} auth={'SCOPE_SM_UPDATE_CAMPAIGN'}/>
                        <ActionButtonWithTooltip type={ActionTypes.details} callback={() => {
                            setOpenDetail(true);
                            setSelectedCouponCampaign(params.data);
                        }} auth={'SCOPE_SM_VIEW_CAMPAIGN_DETAIL'}/>
                        <ActionButtonWithTooltip type={ActionTypes.codes} callback={() => {
                            //navigate(`/salesmarketing/couponcampaigncodes/${params.data.id}`, {state: {promo: params.data}})
                            window.open(`/salesmarketing/couponcampaigncodes/${params.data.id}`, "_blank")
                        }} auth={'SCOPE_SM_COUPON'}/>
                    </Stack>
                );
            }
        }
    ];

    useEffect(() => {
        gridRef?.current?.api?.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
    }, [filters]);

    function createDatasource(filters: CouponInputGetAll, api: GridApi) {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let filterTemp = {...filters};
                filterTemp.offset = params.request.startRow ? params.request.startRow : 0;
                filterTemp.limit = (params.request.endRow ? params.request.endRow : 10) - filterTemp.offset;
                filterTemp.sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                filterTemp.order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;
                filterTemp.search = searchInput;
                let data = await getCouponCampaignList(filterTemp);
                params.success({
                    rowData: data?.content,
                    rowCount: data?.totalElements
                });
                if (data.totalElements === 0) {
                    api.showNoRowsOverlay();
                } else {
                    api.hideOverlay();
                }
            }
        };
    };

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        suppressRowClickSelection: true,
        domLayout: 'autoHeight',
        rowModelType: 'serverSide',
        defaultColDef: {
            sortable: true,
            resizable: true,
            minWidth: 130
        },
        columnDefs: columns,
        noRowsOverlayComponentParams: {title: t('coupon.noRows')}
    };

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);


    return (
        <>
            <SectionHeader title={t('coupon.listHeader.title')} subtitle={t('coupon.listHeader.subTitle')}/>
            <AdvancedFilter validationObj={validationObj} defaultValues={defaultFilters} fields={fields}
                            searchInput={searchInput} setSearchInput={setSearchInput} filters={filters}
                            setFilters={setFilters}/>
            <Grid container>
                <Grid item xs={12} sx={{padding: "16px"}}>
                    <AgGridCustomTable
                        onGridReady={(params) => {
                            params.api.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
                        }}
                        noRowsOverlayComponent={noRowsOverlayComponent} gridOptions={gridOptions}
                        gridRef={gridRef} toolbar={toolbar}/>
                </Grid>
            </Grid>
            {selectedCouponCampaign && <CouponCampaignDetailModal key={selectedCouponCampaign.id} open={openDetail}
                                                                  handleClose={() => setOpenDetail(false)}
                                                                  couponCampaign={selectedCouponCampaign}/>}
        </>
    );
}

export default CouponCampaignListPage;